import { useMemo, type FormEventHandler, type RefObject } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import config from 'constants/config'
import { PENDING_ACTIONS, type PendingAction } from '../constants'
import { ErrorBanner, OrDivider, PendingButtonContent, renderStrongText, StepBackButton, StyledPasswordInput, Title } from './shared'

interface Props {
  mode: 'loginPassword' | 'loginPasswordOTP'
  user: App.User
  autoFocus: boolean
  error: any
  processing: boolean
  recaptchaRef: RefObject<ReCAPTCHA | null>
  onBack: () => void
  onPasswordLogin: FormEventHandler<HTMLFormElement>
  onForgotPassword: () => void
  onSendLoginOtp: () => void
  isActionPending: (action: PendingAction) => boolean
}

function PasswordLoginStep({
  mode,
  user,
  autoFocus,
  error,
  processing,
  recaptchaRef,
  onBack,
  onPasswordLogin,
  onForgotPassword,
  onSendLoginOtp,
  isActionPending,
}: Props) {
  const intl = useIntl()

  const loginWithPasswordLabel = intl.formatMessage({
    id: 'fh6_OdnO5rJp2gw_xexn',
    defaultMessage: 'Log in with password',
    description: 'Button text to log in with password when phone OTP is also available',
  })

  const loginLabel = intl.formatMessage({
    id: 'WGSdj5cWhYv6Jl_p_sHZ',
    defaultMessage: 'Log in',
    description: 'Action button label to log in with password in the account access section',
  })

  const smsSentMessage = useMemo(() => (
    <FormattedMessage
      id="q6kqdRp614wi1ZHGkcl_"
      defaultMessage="SMS will be sent to <strong>{maskedPhone}</strong>"
      description="Help text showing where the OTP SMS will be sent in the account access section"
      values={{
        maskedPhone: user.maskedPhone,
        strong: renderStrongText,
      }}
    />
  ), [user.maskedPhone])

  return (
    <>
      <StepBackButton
        label={intl.formatMessage({
          id: '_I-T4-bL3TaYsTZhGL9E',
          defaultMessage: 'Back',
          description: 'Back button label for embedded account access chat widget',
        })}
        onClick={onBack}
      />
      <Title>{intl.formatMessage({
        id: 'WuJYJAemPZ_x_7m9vTZa',
        defaultMessage: 'Welcome back',
        description: 'Title for embedded password login step in trip planner chat',
      })}
      </Title>
      <VerticalSpacer as="form" gap={16} onSubmit={onPasswordLogin}>
        <ErrorBanner
          error={error}
          genericMessage={intl.formatMessage({
            id: 'fru8EaNI9dfGrLr7HbmQ',
            defaultMessage: 'Something went wrong. Please try again.',
            description: 'Generic server error message for embedded trip planner account access',
          })}
        />
        <StyledPasswordInput
          label={intl.formatMessage({
            id: '_E7EubEmaia87hh9opD2',
            defaultMessage: 'Password',
            description: 'Label for password input on login form on the login or signup page',
          })}
          id="embedded-password"
          required
          name="password"
          placeholder={intl.formatMessage({
            id: 'olLJPmhirn8DzF2ZMTID',
            defaultMessage: 'Password',
            description: 'Placeholder for password input on login form when field is empty',
          })}
          requiredErrorMessage={intl.formatMessage({
            id: 'nw3rYlmMfCzBrPT_YjMq',
            defaultMessage: 'Password is required',
            description: 'Form validation error when password is empty on login',
          })}
          autoFocus={autoFocus}
        />
        <TextButton kind="primary" shape="round" size="medium" fit="flex" type="submit" disabled={processing}>
          <PendingButtonContent
            pending={isActionPending(PENDING_ACTIONS.loginPassword)}
            label={mode === 'loginPasswordOTP' ? loginWithPasswordLabel : loginLabel}
          />
        </TextButton>
      </VerticalSpacer>
      <Group direction="vertical" gap={12} horizontalAlign="center">
        {user.email && (
          <TextLink size="medium" onClick={onForgotPassword}>
            {intl.formatMessage({
              id: 'N5DZh5Kzf6epuxAsDSK1',
              defaultMessage: 'Forgot your password?',
              description: 'Link text to initiate password reset on login form',
            })}
          </TextLink>
        )}
        {mode === 'loginPasswordOTP' && user.maskedPhone && (
          <Group direction="vertical" gap={12} horizontalAlign="center" fullWidth>
            <OrDivider>
              <BodyText colour="light-foreground-alt" variant="medium">
                {intl.formatMessage({
                  id: 'ap5sfJYsVZqznGZiIise',
                  defaultMessage: 'or',
                  description: 'Divider text between password login and OTP login options',
                })}
              </BodyText>
            </OrDivider>
            <TextButton
              kind="secondary"
              size="medium"
              fit="flex"
              onClick={onSendLoginOtp}
              disabled={processing}
            >
              <PendingButtonContent
                pending={isActionPending(PENDING_ACTIONS.sendLoginOtp)}
                label={intl.formatMessage({
                  id: 'LiU3RpkeJG7SccReFhLp',
                  defaultMessage: 'Send me a verification code',
                  description: 'Button text to request OTP verification code via SMS',
                })}
              />
            </TextButton>
            <BodyText variant="medium" colour="light-foreground-alt">
              {smsSentMessage}
            </BodyText>
          </Group>
        )}
      </Group>
      {Boolean(config.RECAPTCHA_KEY) && (
        <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />
      )}
    </>
  )
}

export default PasswordLoginStep
